class DorsaHeader extends HTMLElement {
  connectedCallback() {
    this.toggleButton =
      this.querySelector('[data-dh-toggle]');

    this.sheet =
      this.querySelector('[data-dh-sheet]');

    this.backdrop =
      this.querySelector('[data-dh-backdrop]');

    this.tabs = Array.from(
      this.querySelectorAll('[data-dh-tab]')
    );

    this.panels = Array.from(
      this.querySelectorAll('[data-dh-panel]')
    );

    this.previewTriggers = Array.from(
      this.querySelectorAll(
        '[data-dh-preview-trigger]'
      )
    );

    this.previews = Array.from(
      this.querySelectorAll(
        '[data-dh-preview]'
      )
    );

    this.fallback =
      this.querySelector(
        '[data-dh-fallback]'
      );

    this.inlineOpeners = Array.from(
      this.querySelectorAll(
        '[data-dh-inline-open]'
      )
    );

    this.groups = Array.from(
      this.querySelectorAll(
        '.dorsa-mobile-group'
      )
    );

    this.behaviour =
      this.dataset.behaviour ||
      'dynamic';

    this.hideAfter =
      Number(
        this.dataset.hideAfter ||
        120
      );

    this.sensitivity =
      Number(
        this.dataset.sensitivity ||
        10
      );

    this.idleReveal =
      Number(
        this.dataset.idleReveal ||
        0
      );

    this.lastY =
      window.scrollY;

    this.directionAnchor =
      this.lastY;

    this.lastDirection =
      null;

    this.navOpen =
      false;

    this.idleTimer =
      null;

        this.metricsFrame =
      null;


    /*
      Keeps the thin desktop nav and the
      open mega-menu panel in sync.
    */

    this.activeParentKey =
      null;


    /*
      Adds a larger, more forgiving desktop
      nav hit area and makes the gold active
      underline persist while its menu is open.
    */

    this.installInlineNavReliabilityStyles();


    /* ----------------------------------------
       Static mode
       ---------------------------------------- */

    if (
      this.behaviour ===
      'static'
    ) {
      this.classList.add(
        'is-static'
      );
    }


    /* ----------------------------------------
       Bind methods
       ---------------------------------------- */

    this.onScroll =
      this.onScroll.bind(this);

    this.onResize =
      this.onResize.bind(this);

    this.onKeydown =
      this.onKeydown.bind(this);


    /* ----------------------------------------
       Main menu button
       ---------------------------------------- */

    this.toggleButton
      ?.addEventListener(
        'click',
        () => {
          if (this.navOpen) {
            this.closeNav();
          } else {
            this.openNav();
          }
        }
      );


    /* ----------------------------------------
       Backdrop
       ---------------------------------------- */

    this.backdrop
      ?.addEventListener(
        'click',
        () => {
          this.closeNav();
        }
      );


    /* ----------------------------------------
       Desktop parent navigation
       ---------------------------------------- */

    this.tabs.forEach(
      (tab) => {

        const activate = () => {
          this.activateParent(
            tab.dataset.dhTab
          );
        };

        tab.addEventListener(
          'click',
          activate
        );

        tab.addEventListener(
          'mouseenter',
          activate
        );

        tab.addEventListener(
          'focus',
          activate
        );
      }
    );


        /* ----------------------------------------
       Optional inline navigation

       Desktop:
       - hover reliably opens the menu
       - moving between nav items switches panel
       - click opens instead of navigating
       - focus works for keyboard users

       Touch/mobile:
       - stays click driven
       ---------------------------------------- */

    this.inlineOpeners.forEach(
      (button) => {

        const key =
          button.dataset
            .dhInlineOpen;


        if (!key) {
          return;
        }


        button.setAttribute(
          'aria-haspopup',
          'true'
        );


        button.setAttribute(
          'aria-expanded',
          'false'
        );


        const openOrSwitch =
          () => {

            /*
              If the sheet is already open,
              switching parent directly is
              cleaner than reopening it.
            */

            if (
              this.navOpen
            ) {

              this.activateParent(
                key
              );


              this.scheduleMetrics();

            } else {

              this.openNav(
                key
              );
            }
          };


        /*
          Real desktop hover behaviour.

          Previously these inline nav items
          only listened for click, even though
          the visual design suggested hover.
        */

        button.addEventListener(
          'mouseenter',
          () => {

            const canHover =
              window.matchMedia(
                '(hover: hover) and (pointer: fine)'
              ).matches;


            if (!canHover) {
              return;
            }


            openOrSwitch();
          }
        );


        /*
          Keyboard users get the same behaviour.
        */

        button.addEventListener(
          'focus',
          () => {

            if (
              window.innerWidth >=
              750
            ) {

              openOrSwitch();
            }
          }
        );


        /*
          IMPORTANT:

          Prevent the underlying parent link /
          button from navigating or refreshing
          when its real job is to open a menu.
        */

        button.addEventListener(
          'click',
          (event) => {

            event.preventDefault();

            event.stopPropagation();


            openOrSwitch();
          }
        );
      }
    );


    /* ----------------------------------------
       Collection image previews
       ---------------------------------------- */

    this.previewTriggers.forEach(
      (trigger) => {

        const activate = () => {

          this.activatePreview(
            trigger.dataset
              .dhPreviewTrigger
          );
        };

        trigger.addEventListener(
          'mouseenter',
          activate
        );

        trigger.addEventListener(
          'focus',
          activate
        );
      }
    );


    /* ----------------------------------------
       Mobile accordion behaviour
       Only one group open at once
       ---------------------------------------- */

    this.groups.forEach(
      (group) => {

        group.addEventListener(
          'toggle',
          () => {

            if (!group.open) {
              return;
            }

            this.groups.forEach(
              (other) => {

                if (
                  other !== group
                ) {
                  other.removeAttribute(
                    'open'
                  );
                }
              }
            );
          }
        );
      }
    );


    /* ----------------------------------------
       Global listeners
       ---------------------------------------- */

    window.addEventListener(
      'resize',
      this.onResize
    );

    document.addEventListener(
      'keydown',
      this.onKeydown
    );

    if (
      this.behaviour !==
      'static'
    ) {

      window.addEventListener(
        'scroll',
        this.onScroll,
        {
          passive: true
        }
      );
    }


    /* ----------------------------------------
       ResizeObserver

       Watches compact height / editor setting
       changes without hammering the browser.
       ---------------------------------------- */

    if (
      'ResizeObserver'
      in window
    ) {

      this.resizeObserver =
        new ResizeObserver(
          () => {
            this.scheduleMetrics();
          }
        );

      const surface =
        this.querySelector(
          '.dorsa-header-surface'
        );

      if (surface) {

        this.resizeObserver
          .observe(surface);
      }
    }


    /* ----------------------------------------
       Initialise
       ---------------------------------------- */

    this.activateFirstParent();

    this.updateScrolledState();

    this.scheduleMetrics();

    this.bindThemeEditor();
  }


  disconnectedCallback() {

    window.removeEventListener(
      'resize',
      this.onResize
    );

    window.removeEventListener(
      'scroll',
      this.onScroll
    );

    document.removeEventListener(
      'keydown',
      this.onKeydown
    );

    this.resizeObserver
      ?.disconnect();

    window.clearTimeout(
      this.idleTimer
    );

    if (
      this.metricsFrame
    ) {

      cancelAnimationFrame(
        this.metricsFrame
      );
    }
  }

  /* ========================================================
     INLINE NAV RELIABILITY
     ======================================================== */

  installInlineNavReliabilityStyles() {

    /*
      Only install once even if Shopify
      re-renders the header in Theme Editor.
    */

    if (
      document.getElementById(
        'DorsaInlineNavReliabilityStyles'
      )
    ) {

      return;
    }


    const style =
      document.createElement(
        'style'
      );


    style.id =
      'DorsaInlineNavReliabilityStyles';


    style.textContent = `
      /*
        Give each thin desktop nav item a much
        more forgiving pointer target.

        The visual text position barely changes,
        but users no longer need to hover on the
        exact letters for the menu to respond.
      */

      [data-dh-inline-open] {
        position: relative !important;
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
        min-height: 46px !important;
        padding-left: 10px !important;
        padding-right: 10px !important;
        margin-left: -4px !important;
        margin-right: -4px !important;
        cursor: pointer !important;
        -webkit-tap-highlight-color: transparent;
      }


      /*
        The active menu keeps its DORSA gold
        underline even after the pointer moves
        from the nav label into the mega menu.
      */

      [data-dh-inline-open].active::after,
      [data-dh-inline-open].is-active::after {
        opacity: 1 !important;
        visibility: visible !important;
        transform: scaleX(1) !important;
      }


      /*
        Keep hover/focus visual feedback aligned
        with the larger functional hit area.
      */

      @media (hover: hover) and (pointer: fine) {

        [data-dh-inline-open]:hover::after,
        [data-dh-inline-open]:focus-visible::after {
          opacity: 1 !important;
          visibility: visible !important;
          transform: scaleX(1) !important;
        }
      }
    `;


    document.head.appendChild(
      style
    );
  }


  syncInlineOpeners(key) {

    this.inlineOpeners.forEach(
      (button) => {

        const active =
          Boolean(
            this.navOpen &&
            key &&
            button.dataset
              .dhInlineOpen ===
              key
          );


        /*
          Use both class names.

          "active" remains consistent with the
          rest of the existing header code and
          "is-active" gives the reliability CSS
          an explicit state hook.
        */

        button.classList.toggle(
          'active',
          active
        );


        button.classList.toggle(
          'is-active',
          active
        );


        button.setAttribute(
          'aria-expanded',
          active
            ? 'true'
            : 'false'
        );
      }
    );
  }
  /* ========================================================
     METRICS
     ======================================================== */

  scheduleMetrics() {

    if (
      this.metricsFrame
    ) {

      cancelAnimationFrame(
        this.metricsFrame
      );
    }

    this.metricsFrame =
      requestAnimationFrame(
        () => {

          this.metricsFrame =
            null;

          this.updateMetrics();
        }
      );
  }


  updateMetrics() {

    const surface =
      this.querySelector(
        '.dorsa-header-surface'
      );

    if (!surface) {
      return;
    }


    /*
      offsetHeight gives the actual header
      surface height without transforms.
    */

    const headerHeight =
      Math.ceil(
        surface.offsetHeight
      );


    /*
      getBoundingClientRect().bottom gives
      the REAL bottom of the header in
      viewport coordinates.

      At the top of the page this naturally
      includes the announcement bar above it.

      Once the announcement bar has scrolled
      away and the header is sticky, this
      naturally becomes ~60–72px.
    */

    const surfaceRect =
      surface
        .getBoundingClientRect();


    const sheetTop =
      Math.max(
        0,
        Math.round(
          surfaceRect.bottom
        )
      );


    this.style.setProperty(
      '--dh-stack',
      `${headerHeight}px`
    );


    this.style.setProperty(
      '--dh-sheet-top',
      `${sheetTop}px`
    );


    document.documentElement
      .style
      .setProperty(
        '--header-height',
        `${headerHeight}px`
      );
  }


  /* ========================================================
     SCROLL
     ======================================================== */

  updateScrolledState() {

    const scrolled =
      window.scrollY > 8;


    this.classList.toggle(
      'is-scrolled',
      scrolled
    );


    this.scheduleMetrics();
  }


  onScroll() {

    if (
      this.navOpen
    ) {
      return;
    }


    const y =
      window.scrollY;


    const delta =
      y - this.lastY;


    this.updateScrolledState();


    /* Always-visible mode */

    if (
      this.behaviour ===
      'always'
    ) {

      this.classList.remove(
        'is-hidden'
      );

      this.lastY = y;

      return;
    }


    /* Static is handled by CSS */

    if (
      this.behaviour !==
      'dynamic'
    ) {

      this.lastY = y;

      return;
    }


    /* Always visible at page top */

    if (
      y <= 8
    ) {

      this.classList.remove(
        'is-hidden'
      );

      this.directionAnchor =
        y;

      this.lastDirection =
        null;

      this.lastY =
        y;

      return;
    }


    /*
      Ignore tiny browser jitter.
    */

    if (
      Math.abs(delta) < 1
    ) {

      this.lastY =
        y;

      return;
    }


    const direction =
      delta > 0
        ? 'down'
        : 'up';


    /*
      Reset anchor whenever scrolling
      direction changes.
    */

    if (
      direction !==
      this.lastDirection
    ) {

      this.lastDirection =
        direction;

      this.directionAnchor =
        y;
    }


    const moved =
      Math.abs(
        y -
        this.directionAnchor
      );


    /*
      Wait for meaningful movement before
      changing header visibility.
    */

    if (
      moved >=
      this.sensitivity
    ) {

      if (
        direction ===
          'down' &&
        y >
          this.hideAfter
      ) {

        this.classList.add(
          'is-hidden'
        );
      }


      if (
        direction ===
        'up'
      ) {

        this.classList.remove(
          'is-hidden'
        );
      }
    }


    /*
      Optional idle reveal.
    */

    if (
      this.idleReveal > 0
    ) {

      window.clearTimeout(
        this.idleTimer
      );


      this.idleTimer =
        window.setTimeout(
          () => {

            if (
              !this.navOpen &&
              window.scrollY > 8
            ) {

              this.classList.remove(
                'is-hidden'
              );
            }

          },
          this.idleReveal
        );
    }


    this.lastY =
      y;
  }


  /* ========================================================
     NAVIGATION
     ======================================================== */

  openNav(parentKey) {

    this.navOpen =
      true;


    /*
      Remove hidden state FIRST so the
      surface is physically back in place
      before we calculate menu position.
    */

    this.classList.remove(
      'is-hidden'
    );


    this.classList.add(
      'is-nav-open',
      'is-scrolled'
    );


    this.toggleButton
      ?.setAttribute(
        'aria-expanded',
        'true'
      );


    this.sheet
      ?.setAttribute(
        'aria-hidden',
        'false'
      );


    document.body
      .classList
      .add(
        'dorsa-nav-lock'
      );


        this.activateParent(
      parentKey ||
      this.activeParentKey ||
      this.tabs[0]
        ?.dataset
        .dhTab
    );


    /*
      Two RAFs allow the header transform
      and compact state to settle before
      we measure the viewport position.
    */

    requestAnimationFrame(
      () => {

        requestAnimationFrame(
          () => {

            this.updateMetrics();
          }
        );
      }
    );
  }


  closeNav() {

    if (
      !this.navOpen
    ) {
      return;
    }


    this.navOpen =
      false;


    this.classList.remove(
      'is-nav-open'
    );


    this.toggleButton
      ?.setAttribute(
        'aria-expanded',
        'false'
      );


    this.sheet
      ?.setAttribute(
        'aria-hidden',
        'true'
      );


        document.body
      .classList
      .remove(
        'dorsa-nav-lock'
      );


    /*
      Remove the persistent active underline
      when the navigation itself is closed.
    */

    this.syncInlineOpeners(
      null
    );


    this.updateScrolledState();


    this.toggleButton
      ?.focus({
        preventScroll: true
      });
  }


  /* ========================================================
     PARENT NAVIGATION
     ======================================================== */

  activateFirstParent() {

    const first =
      this.tabs[0]
        ?.dataset
        .dhTab;


    if (first) {

      this.activateParent(
        first
      );
    }
  }


    activateParent(key) {

    if (!key) {
      return;
    }


    /*
      Remember the active parent so hover,
      click, gold underline and visible panel
      always represent the same menu.
    */

    this.activeParentKey =
      key;


    this.syncInlineOpeners(
      key
    );


    this.tabs.forEach(
      (tab) => {

        const active =
          tab.dataset.dhTab ===
          key;


        tab.classList.toggle(
          'active',
          active
        );


        tab.setAttribute(
          'aria-current',
          active
            ? 'true'
            : 'false'
        );
      }
    );


    this.panels.forEach(
      (panel) => {

        panel.hidden =
          panel.dataset
            .dhPanel !==
          key;
      }
    );


    const activePanel =
      this.panels.find(
        (panel) =>

          panel.dataset
            .dhPanel ===
          key
      );


    const firstTrigger =
      activePanel
        ?.querySelector(
          '[data-dh-preview-trigger]'
        );


    if (firstTrigger) {

      this.activatePreview(
        firstTrigger
          .dataset
          .dhPreviewTrigger
      );

    } else {

      this.activatePreview(
        null
      );
    }
  }


  /* ========================================================
     COLLECTION PREVIEW
     ======================================================== */

  activatePreview(key) {

    let matched =
      false;


    this.previews.forEach(
      (preview) => {

        const active =
          preview.dataset
            .dhPreview ===
          key;


        preview.classList.toggle(
          'active',
          active
        );


        if (active) {

          matched =
            true;
        }
      }
    );


    if (
      this.fallback
    ) {

      this.fallback
        .classList
        .toggle(
          'active',
          !matched
        );
    }
  }


  /* ========================================================
     WINDOW / KEYBOARD
     ======================================================== */

  onResize() {

    this.scheduleMetrics();


    /*
      Close mobile accordions when moving
      back to desktop.
    */

    if (
      window.innerWidth >=
      750
    ) {

      this.groups.forEach(
        (group) => {

          group.removeAttribute(
            'open'
          );
        }
      );
    }
  }


  onKeydown(event) {

    if (
      event.key ===
        'Escape' &&
      this.navOpen
    ) {

      event.preventDefault();

      this.closeNav();

      return;
    }


    /*
      Trap Tab inside the open menu.
    */

    if (
      event.key !== 'Tab' ||
      !this.navOpen ||
      !this.sheet
    ) {

      return;
    }


    const focusable =
      Array.from(
        this.sheet
          .querySelectorAll(
            [
              'a[href]',
              'button:not([disabled])',
              'summary',
              'input:not([disabled])',
              'select:not([disabled])',
              '[tabindex]:not([tabindex="-1"])'
            ].join(',')
          )
      )
      .filter(
        (element) =>

          element.offsetParent !==
          null
      );


    if (
      focusable.length ===
      0
    ) {

      return;
    }


    const first =
      focusable[0];


    const last =
      focusable[
        focusable.length - 1
      ];


    if (
      event.shiftKey &&
      document.activeElement ===
        first
    ) {

      event.preventDefault();

      last.focus();

    } else if (
      !event.shiftKey &&
      document.activeElement ===
        last
    ) {

      event.preventDefault();

      first.focus();
    }
  }


  /* ========================================================
     SHOPIFY THEME EDITOR
     ======================================================== */

  bindThemeEditor() {

    document.addEventListener(
      'shopify:block:select',
      (event) => {

        const selected =
          event.target;


        if (
          !selected ||
          !this.contains(
            selected
          )
        ) {

          return;
        }


        const figure =
          selected.closest?.(
            '[data-dh-block]'
          );


        if (!figure) {

          return;
        }


        const key =
          figure.dataset
            .dhPreview;


        const parent =
          key
            ?.split('--')[0];


        this.openNav(
          parent
        );


        if (key) {

          this.activatePreview(
            key
          );
        }
      }
    );


    document.addEventListener(
      'shopify:section:select',
      (event) => {

        if (
          event.target
            ?.contains?.(
              this
            )
        ) {

          this.classList.remove(
            'is-hidden'
          );


          this.scheduleMetrics();
        }
      }
    );
  }
}


if (
  !customElements.get(
    'dorsa-header'
  )
) {

  customElements.define(
    'dorsa-header',
    DorsaHeader
  );
}